import { SelectProps } from 'antd';
import { FormProps } from 'antd/lib';
import { useEffect, useMemo } from 'react';
import { useOutletContext } from 'react-router';

import useFormInitialSet from '@/hooks/useFormInitialSet';
import { useSelectScrollSearch } from '@/hooks/useSelectScrollSearch';
import { useSelectPropsWithInitial } from '@/hooks/useSelectWithInitialValue';
import { RESOURCES } from '@/Resources';

const NUMERIC_FILTER_FIELDS = new Set([
  'company',
  'company_type',
  'shipment_payment_company',
  'shipper',
  'created_by',
  'ticket',
  'tipo',
]);

export const usePackagesListFilter = (formProps: FormProps, filters: any[]) => {
  const { form } = formProps;
  const initialValues: Record<string, any> = useMemo(
    () =>
      Object.fromEntries(
        filters.map(filter => {
          if (filter.field === 'status' && typeof filter.value === 'string') {
            const statusValues = filter.value.split(',').reduce((acc, value) => {
              const parsedValue = parseInt(value, 10);

              if (!isNaN(parsedValue)) {
                acc.push(parsedValue);
              }

              return acc;
            }, []);

            return [filter.field, statusValues];
          }

          if (NUMERIC_FILTER_FIELDS.has(filter.field)) {
            const valueParsed = parseInt(filter.value, 10);
            const value = isNaN(valueParsed) ? filter.value : valueParsed;

            return [filter.field, value];
          }

          return [filter.field, filter.value];
        }),
      ),
    [filters],
  );
  const { setInitialValuesSetted } = useFormInitialSet(form, initialValues, [
    {
      watchField: 'company',
      resetFields: ['company_type'],
    },
  ]);

  useEffect(() => {
    setInitialValuesSetted(false);
  }, [initialValues, setInitialValuesSetted]);

  const { companySelectProps, shippersSelectProps, ticketsSelectProps } = useOutletContext<{
    companySelectProps: SelectProps;
    shippersSelectProps: SelectProps;
    ticketsSelectProps: SelectProps;
  }>();
  const { selectProps: usersSelectProps } = useSelectScrollSearch({
    resource: 'users',
    optionLabel: (item: any) => `${item.first_name} ${item.last_name} (${item.username})`,
    pagination: {
      mode: 'off',
      pageSize: 3000,
    },
    meta: {
      empresa: 4,
    },
  });
  const ticketsSelectPropsWithInitials = useSelectPropsWithInitial({
    selectProps: ticketsSelectProps,
    resource: RESOURCES.TICKETS,
    initialValue: formProps.initialValues?.referencia,
    labelKey: (item: any) =>
      `${item.id} - ${item.title ? item.title : item.ticket_title_str ? item.ticket_title_str : '-'}`,
    valueKey: 'id',
  });
  const companySelectPropsWithInitials = useSelectPropsWithInitial({
    selectProps: companySelectProps,
    resource: 'empresas',
    initialValue:
      initialValues.company === initialValues.shipment_payment_company
        ? [initialValues.company]
        : [initialValues.company, initialValues.shipment_payment_company],
    labelKey: 'full_name',
    valueKey: 'id',
  });
  const shippersSelectPropsWithInitials = useSelectPropsWithInitial({
    selectProps: shippersSelectProps,
    resource: RESOURCES.SHIPPERS,
    initialValue: initialValues.shipper,
    labelKey: 'name',
    valueKey: 'id',
  });
  const usersSelectPropsWithInitials = useSelectPropsWithInitial({
    selectProps: usersSelectProps,
    resource: 'users',
    initialValue: initialValues.created_by,
    labelKey: (item: any) => `${item.first_name} ${item.last_name} (${item.username})`,
    valueKey: 'id',
  });

  return {
    companySelectProps: companySelectPropsWithInitials,
    shippersSelectProps: shippersSelectPropsWithInitials,
    usersSelectProps: usersSelectPropsWithInitials,
    ticketsSelectProps: ticketsSelectPropsWithInitials,
  };
};
